<template>
  <div
    class="fapi-order-form"
    :id="$id('fapi-form')"
    :data-path="path"
    :style="stylesVariables"
  >
    <Alert v-if="showError" :message="$t('form.unable_to_load_form')"></Alert>
    <Alert
      v-if="securityTokenExpired"
      v-show="false"
      :message="$t('form.security_token_expired')"
    ></Alert>

    <template v-if="formResource">
      <Alert v-if="formResource.blocked" :message="$t('form.blocked')"></Alert>
      <Alert v-if="formResource.deleted" :message="$t('form.deleted')"></Alert>
      <Alert
        v-if="formResource.mioweb_eshop"
        :message="$t('form.is_mw_eshop')"
      ></Alert>
      <Warning
        v-if="
          !formResource.deleted && !formResource.blocked && formResource.test
        "
        :message="$t('form.test')"
      >
      </Warning>

      <FapiForm
        :formResource="formResource"
        :localeLoader="localeLoader"
        :securityToken="securityToken"
        v-if="renderForm"
      ></FapiForm>
      <p
        class="f-mt-4 f-text-center f-text-xs f-text-gray-400"
        v-if="renderForm"
      >
        {{ $t("form.promo") }}
        <a
          :href="`https://fapi.cz/?utm_source=form&utm_medium=link&utm_campaign=promo_bottom&fr_code=${
            formResource?.affiliate_code || 'form'
          }`"
          rel="noopener"
          target="_blank"
          class="f-text-gray-400 hover:f-text-gray-500"
          ><u>FAPI</u></a
        >.
      </p>
    </template>
  </div>
</template>

<script lang="ts">
import FapiForm from "@/components/Form/FapiForm.vue";
import FormResource from "@/module/Form/Resources/FormResource";
import LocaleLoader from "@/module/Utils/LocaleLoader";
import StylesBuilder from "@/module/Css/StylesBuilder";
import FormHelper from "@/module/FormHelper";
import getDefaultTemplateSetting from "@/module/Form/TemplateSettings/DefaultTemplateSetting";
import { mergeDeep } from "@/module/Utils/functions";
import ScriptsLoader from "@/module/Utils/ScriptsLoader";
import Vue, { PropType } from "vue";
import Alert from "@/components/Form/Alert.vue";
import TemplateSettings from "@/module/Form/Resources/TemplateSettings";
import DefaultCountryProvider from "@/module/DefaultCountryProvider";
import Warning from "@/components/Form/Warning.vue";

interface ComponentData {
  formResource: null | FormResource;
  retryCount: number;
  showError: boolean;
  localeLoader: null | LocaleLoader;
  stylesVariables: any;
  securityTokenExpired: boolean;
}

export default Vue.extend({
  name: "FormWrapper",

  components: {
    Warning,
    FapiForm,
    Alert,
  },

  props: {
    path: {
      type: String,
      required: true,
    },
    dataUrl: {
      type: String,
      required: true,
    },
    dataFormResource: {
      type: String,
      required: true,
    },
    securityToken: {
      type: Object as PropType<{
        token: string;
        expiration: string;
      }>,
      required: true,
    },
  },

  data: function (): ComponentData {
    return {
      formResource: null,
      retryCount: 0,
      showError: false,
      localeLoader: null,
      stylesVariables: {
        "text-align": "left",
      },
      securityTokenExpired: false,
    };
  },

  computed: {
    renderForm: function (): boolean {
      if (this.showError) {
        return false;
      }

      return (
        this.formResource !== null &&
        !this.formResource.blocked &&
        !this.formResource.deleted &&
        !this.formResource.mioweb_eshop
      );
    },
  },

  methods: {
    async handleData(json: FormResource | null): Promise<void> {
      if (this.formResource !== null) {
        return;
      }

      this.localeLoader = new LocaleLoader(
        this.$i18n,
        json?.language || "cs",
        json?.settings.currency || "CZK",
        json?.styling?.minimum_fraction_digits ?? 2,
        json?.styling?.maximum_fraction_digits ?? 2,
      );
      const localePromise = this.localeLoader.changeLocale(
        json?.language || "cs",
      );

      if (json === null) {
        this.showError = true;
        console.error("Unable to load form data. Form path: " + this.path);

        throw new Error("Unable to load form data. Form path: " + this.path);
      }

      const defaultCountryPromise = DefaultCountryProvider.provider();

      json.template_settings = mergeDeep(
        getDefaultTemplateSetting(),
        json.template_settings ?? {},
      ) as TemplateSettings;

      if (json.max_width) {
        this.stylesVariables["max-width"] = json.max_width + "px";
      }

      await new StylesBuilder(
        this.stylesVariables,
        json.styling,
      ).fillStylesVariables();

      if (json.allow_custom_css && json.custom_css_url !== null) {
        ScriptsLoader.loadCss(json.custom_css_url);
      }

      await localePromise;
      const translatedCountries: { [key: string]: string } = {};

      for (const index in json.countries) {
        let countryCode = json.countries[index];

        if (countryCode === "CS") {
          delete json.countries[index];

          continue;
        }

        translatedCountries[countryCode] = this.$t(
          "countries." + countryCode,
        ).toString();
      }

      json.countries = translatedCountries;
      json.allowed_countries = FormHelper.sortCountries(
        FormHelper.filterAllowedCountries(json),
        this.$root.$i18n.locale,
      );
      json.default_country = await defaultCountryPromise;

      json.path = this.path;
      this.formResource = json as FormResource;
    },
  },

  mounted(): void {
    this.handleData(JSON.parse(this.dataFormResource));
    let tokenExpiration =
      new Date(this.securityToken.expiration).getTime() - new Date().getTime();

    setTimeout(() => {
      this.securityTokenExpired = true;
    }, tokenExpiration);
  },
});
</script>
